// Rate cadence — how many rate sets a plan year carries, and where their
// boundaries fall (docs/RATE_CADENCE_IMPLEMENTATION_PLAN.md).
//
// A cadence period is a window of PLAN-YEAR STARTS, exactly like any other rate
// version window (see shared/utils/chargeResolution.ts). Cadence adds nothing to
// resolution — it only fixes WHERE the boundaries may fall, so that a year can
// be published whole and an incomplete year rejected instead of silently
// leaving a gap that the prior period swallows.
//
// Boundaries are calendar-anchored and NOT editable: that is the whole point of
// the hard constraint. Pure module — imported by both the server (publishing,
// validation) and the builder UI, so they cannot disagree about a boundary.
//
// Periods publish independently: a carrier files Q1 in December and Q2 in
// March, so a year is built up one period at a time. A period that has not
// been published yet has NO rate — plan years starting in it resolve to
// nothing (unresolved charge, no quote) rather than to a neighbouring period,
// because every published period carries an explicit end and the publisher
// closes the prior year's open window at Jan 1 (see publishRateYear).

export type RateCadence = "annual" | "biannual" | "quarterly";

export const RATE_CADENCES: readonly RateCadence[] = ["annual", "biannual", "quarterly"] as const;

/** First month (1-based) of each period, in order. Length = periods per year. */
const START_MONTHS: Record<RateCadence, readonly number[]> = {
  annual: [1],
  biannual: [1, 7],
  quarterly: [1, 4, 7, 10],
};

const PERIOD_PREFIX: Record<RateCadence, string> = {
  annual: "",
  biannual: "H",
  quarterly: "Q",
};

export function isRateCadence(value: unknown): value is RateCadence {
  return typeof value === "string" && (RATE_CADENCES as readonly string[]).includes(value);
}

export function periodsPerCadence(cadence: RateCadence): number {
  return START_MONTHS[cadence].length;
}

const iso = (year: number, month: number) => `${year}-${String(month).padStart(2, "0")}-01`;

/**
 * The ONLY legal effectiveStarts for a cadence-managed year, in period order.
 * quarterly 2027 → ["2027-01-01","2027-04-01","2027-07-01","2027-10-01"]
 */
export function periodStarts(year: number, cadence: RateCadence): string[] {
  return START_MONTHS[cadence].map((m) => iso(year, m));
}

/**
 * [effectiveStart, effectiveEnd) for a 1-based period index. The last period of
 * a year ends at the next year's Jan 1, so consecutive years tile with no gap.
 */
export function periodWindow(
  year: number,
  cadence: RateCadence,
  periodIndex: number,
): { effectiveStart: string; effectiveEnd: string } {
  const months = START_MONTHS[cadence];
  if (!Number.isInteger(periodIndex) || periodIndex < 1 || periodIndex > months.length) {
    throw new Error(`Period ${periodIndex} is out of range for ${cadence} (1..${months.length})`);
  }
  return {
    effectiveStart: iso(year, months[periodIndex - 1]),
    effectiveEnd:
      periodIndex === months.length ? iso(year + 1, 1) : iso(year, months[periodIndex]),
  };
}

/** Display label: "2027", "H1 2027", "Q3 2027". */
export function periodLabel(year: number, cadence: RateCadence, periodIndex: number): string {
  if (cadence === "annual") return String(year);
  return `${PERIOD_PREFIX[cadence]}${periodIndex} ${year}`;
}

export interface RateYearPeriodProblem {
  code: "DUPLICATE_PERIOD" | "UNKNOWN_PERIOD" | "NO_RATES";
  message: string;
}

/**
 * Periods of `year` that carry no rates yet, in order — for the builder's
 * tabs and the publish response ("Q2, Q3 2027 still unpublished").
 */
export function missingPeriods(
  year: number,
  cadence: RateCadence,
  periods: Array<{ periodIndex: number; hasRates: boolean }>,
): number[] {
  const withRates = new Set(periods.filter((p) => p.hasRates).map((p) => p.periodIndex));
  const out: number[] = [];
  for (let i = 1; i <= periodsPerCadence(cadence); i++) if (!withRates.has(i)) out.push(i);
  return out;
}

/** "Q2 2027, Q3 2027" */
export function missingPeriodsLabel(year: number, cadence: RateCadence, missing: number[]): string {
  return missing.map((i) => periodLabel(year, cadence, i)).join(", ");
}

/**
 * Why a set of periods can't be published into a rate year, or null if it
 * can. Periods publish independently, so an incomplete year is fine — but a
 * submission has to carry at least one usable period, and no period may be
 * duplicated or outside the cadence.
 */
export function findRateYearPeriodProblem(
  year: number,
  cadence: RateCadence,
  periods: Array<{ periodIndex: number; hasRates: boolean }>,
): RateYearPeriodProblem | null {
  const expected = periodsPerCadence(cadence);

  const seen = new Set<number>();
  for (const p of periods) {
    if (seen.has(p.periodIndex)) {
      return { code: "DUPLICATE_PERIOD", message: `Period ${p.periodIndex} was supplied more than once` };
    }
    seen.add(p.periodIndex);
  }

  const unexpected = periods
    .map((p) => p.periodIndex)
    .filter((i) => !Number.isInteger(i) || i < 1 || i > expected);
  if (unexpected.length > 0) {
    return {
      code: "UNKNOWN_PERIOD",
      message: `Period(s) ${unexpected.join(", ")} are out of range for ${cadence} (1..${expected})`,
    };
  }

  if (!periods.some((p) => p.hasRates)) {
    return {
      code: "NO_RATES",
      message: `Nothing to publish for ${cadence} ${year} — no period carries a usable rate`,
    };
  }

  return null;
}

/** The 1-based period containing a plan-year start, or null if outside `year`. */
export function periodIndexForStart(
  year: number,
  cadence: RateCadence,
  isoDate: string,
): number | null {
  const starts = periodStarts(year, cadence);
  const end = iso(year + 1, 1);
  if (isoDate < starts[0] || isoDate >= end) return null;
  let index = 1;
  for (let i = 0; i < starts.length; i++) if (isoDate >= starts[i]) index = i + 1;
  return index;
}
